<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h2>
          <v-icon class="mr-2">
            mdi-playlist-check
          </v-icon>
          売上品目単価
        </h2>
      </v-col>
    </v-row>

    <!-- 検索ブロック -->
    <v-row>
      <v-col>
        <validation-observer ref="SalesItemPriceValidation">
          <v-card>
            <v-card-title class="panel-header-color panel-selectable">
              <strong>
                <small>
                  <v-icon class="panel-header-color">
                    mdi-magnify
                  </v-icon>
                  &nbsp;条件を指定して検索
                </small>
              </strong>
            </v-card-title>
            <v-spacer />
            <v-card-text class="pt-2">
              <v-row>
                <v-col cols="12" md="4">
                  <cs-date-picker
                    :date.sync="searchForm.referenceDate"
                    @dateChangedCallback="referenceDateChangedCallback"
                  >
                    <template v-slot="{ on, attrs }">
                      <validation-provider
                        v-slot="{ errors }"
                        class="requiredMark"
                        rules="required"
                        name="表示基準日"
                      >
                        <v-text-field
                          v-model="searchForm.referenceDate"
                          v-bind="attrs"
                          v-on="on"
                          :error-messages="errors"
                          append-icon="mdi-calendar"
                          dense
                          hide-details="auto"
                          label="表示基準日"
                          clearable
                          outlined
                          persistent-hint
                          persistent-placeholder
                          placeholder="日付を選択"
                          readonly
                          rounded
                          style="font-size: 80%"
                        />
                      </validation-provider>
                    </template>
                  </cs-date-picker>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="searchForm.code"
                    dense
                    hide-details="auto"
                    label="売上品目コード"
                    clearable
                    outlined
                    persistent-placeholder
                    rounded
                    @blur="searchButtonClickEvent()"
                    @click:clear="searchForm.code = null; searchButtonClickEvent()"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="searchForm.name"
                    dense
                    hide-details="auto"
                    label="売上品目名"
                    clearable
                    outlined
                    persistent-placeholder
                    placeholder="部分一致で検索"
                    rounded
                    @blur="searchButtonClickEvent()"
                    @click:clear="searchForm.name = null; searchButtonClickEvent()"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="4">
                  <item-autocomplete
                    v-model="searchForm.itemId"
                    :items="items"
                    classes="search-font"
                    clearable
                    dense
                    label="JWNET取扱品目"
                    persistent-placeholder
                    outlined
                    rounded
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    @change="searchButtonClickEvent()"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="searchForm.salesItemType"
                    :items="salesItemTypes"
                    clearable
                    dense
                    hide-details="auto"
                    label="品目区分"
                    outlined
                    persistent-placeholder
                    rounded
                    style="font-size: 80%"
                    @change="searchButtonClickEvent()"
                  />
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn color="primary" @click="searchButtonClickEvent">
                <v-icon>mdi-magnify</v-icon>&nbsp;検索
              </v-btn>
            </v-card-actions>
          </v-card>
        </validation-observer>
      </v-col>
    </v-row>
    <!-- /検索ブロック -->

    <!-- ボタン -->
    <v-row>
      <v-col cols="3" class="text-left">
        <v-btn
          class="primary"
          @click="switchSupplierSalesItemPriceButtonClickEvent"
        >
          取引先品目単価
        </v-btn>
      </v-col>
      <v-col cols="9" class="text-right">
        <template
          v-if="$vuetify.breakpoint.mdAndUp"
        >
          <v-btn
            color="info"
            @click="exportButtonClickEvent"
          >
            <v-icon>mdi-file-download-outline</v-icon>&nbsp;CSV出力
          </v-btn>
          &nbsp;
          <v-btn
            color="info"
            @click="importButtonClickEvent"
          >
            <v-icon>mdi-file-upload-outline</v-icon>&nbsp;CSV取込
          </v-btn>
        </template>
        <template
          v-else
        >
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="info"
                dark
                v-bind="attrs"
                v-on="on"
              >
                CSVファイル
                <v-icon small>
                  mdi-file-outline
                </v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                @click="exportButtonClickEvent"
              >
                <v-list-item-title>
                  CSV出力
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                @click="importButtonClickEvent"
              >
                <v-list-item-title>
                  CSV取込
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        &emsp;
        <v-btn
          color="primary"
          @click="addButtonClickEvent"
        >
          品目単価の追加&nbsp;<v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <!-- /ボタン -->

    <v-row>
      <v-col>
        <custom-pagination
          :page-number="page"
          :per-page="perPage"
          :pagination="pagination"
          :total-visible="totalVisible"
          @pageChanged="changePage"
          @perPageChanged="changePerPage"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-card flat class="rounded-card">
          <v-card-text>
            <v-data-table
              :headers="tableHeaders"
              :items="salesItemPrices"
              :items-per-page="-1"
              dense
              disable-sort
              hide-default-footer
            >
              <template v-slot:[`item.item`]="{ item }">
                <span v-if="item.item.code">
                  {{ item.item.code }} : {{ item.item.name | truncate(10) }}
                </span>
              </template>
              <template v-slot:[`item.reference_date`]="{ item }">
                {{ item.reference_date | date_format }}
              </template>
              <template v-slot:[`item.unit_name`]="{ item }">
                {{ item.unit_name | truncate(15) }}
              </template>
              <template v-slot:[`item.transport_route_name`]="{ item }">
                <span v-if="item.transport_route_name && item.invisible_route" class="red--text text-sm-caption">●&nbsp;</span>
                {{ item.transport_route_name | truncate(10) }}
              </template>
              <template v-slot:[`item.sales_item_type`]="{ item }">
                {{ item.sales_item_type | sales_item_type }}
              </template>
              <template v-slot:[`item.price`]="{item}">
                {{ item.price | comma_format }}
              </template>
              <template v-slot:[`item.editable`]="{item}">
                <v-btn
                  :icon="$vuetify.breakpoint.mdAndUp"
                  :text="$vuetify.breakpoint.smAndDown"
                  :color="`info ${$vuetify.breakpoint.mdAndUp ? 'darken-2': null}`"
                  @click="editButtonClickEvent(item)"
                >
                  <v-icon v-if="$vuetify.breakpoint.mdAndUp">
                    mdi-note-edit
                  </v-icon>
                  <span v-else>編集</span>
                </v-btn>

                <v-btn
                  :icon="$vuetify.breakpoint.mdAndUp"
                  :text="$vuetify.breakpoint.smAndDown"
                  :color="`error ${$vuetify.breakpoint.mdAndUp ? 'darken-2': null}`"
                  @click="deleteButtonClickEvent(item)"
                >
                  <v-icon v-if="$vuetify.breakpoint.mdAndUp">
                    mdi-trash-can-outline
                  </v-icon>
                  <span v-else>削除</span>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- ページャ -->
    <v-row>
      <v-col>
        <custom-pagination
          :page-number="page"
          :per-page="perPage"
          :pagination="pagination"
          :total-visible="totalVisible"
          @pageChanged="changePage"
          @perPageChanged="changePerPage"
        />
      </v-col>
    </v-row>
    <!-- /ページャ -->

    <!-- /ダイアログ -->
    <itemPrice-regist-dialog
      :enable-dialog="enableItemPriceDialog"
      :target-id="targetId"
      :items="items"
      :sales-item-units="salesItemUnits"
      :packings="packings"
      :hazardous-substances="hazardousSubstances"
      :transport-routes="transportRoutes"
      @closeEvent="closeItemPriceDialog"
      @registerEvent="itemPriceCallback"
    />
    <!-- ダイアログ -->

    <confirm ref="confirm" />
  </v-container>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex'
import Confirm from '../../../../components/Confirm'
import ItemPriceRegistDialog from './dialogs/ItemPriceRegistDialog'
import DownloadMixin from '../../../../mixins/download-mixin'
import CustomPagination from "@/driver/components/_CustomPagination.vue";
import ItemAutocomplete from "@/driver/components/ItemAutocomplete.vue";

export default {
  name: 'SalesItemPriceIndex',
  components: { 
    CustomPagination, 
    Confirm, 
    ItemPriceRegistDialog, 
    ItemAutocomplete
  },
  mixins: [
    DownloadMixin,
  ],
  data () {
    return {
      page: 1,
      perPage: 50,
      canUseAddButton: false,
      totalVisible: 7,
      withDeleted: false,
      targetId: null,
      enableItemPriceDialog: false,
      searchForm: {
        referenceDate: null,
        code: null,
        name: null,
        itemId: null,
        salesItemType: null,
      },
      tableHeaders: [
        { text: '売上品目コード', value: 'code' },
        { text: '売上品目名', value: 'name' },
        { text: '改定年月日', value: 'reference_date' },
        { text: 'JWNET取扱品目', value: 'item' },
        { text: '標準単価', value: 'price', align: 'end' },
        { text: '単位', value: 'unit_name' },
        { text: 'ルート', value: 'transport_route_name' },
        { text: '品目区分', value: 'sales_item_type' },
        { text: '並び順', value: 'sort_priority', align: 'end' },
        { text: '編集・削除', value: 'editable', align: 'end' },
      ],
      salesItemTypes: [
        { value: 'jwnet_item', text: '処理品目'},
        { value: 'cost_item', text: '経費品目'},
        { value: 'purchase_item', text: '仕入品目'}
      ],
      salesItemPrices: [],
      items: [],
      salesItemUnits: [],
      packings: [],
      hazardousSubstances: [],
      transportRoutes: [],
    }
  },
  computed: {
    ...mapState('DriverMasterSalesItemPrice', {
      pagination: 'pagination',
      searchCondition: 'condition',
    }),
    ...mapGetters('DriverMasterSalesItemPrice', [
      'hasResult'
    ]),
  },
  watch: {},
  created() {
    if (this.$route.params.resetCondition) {
      this.resetState()
    }
    if (this.$route.params.resetPagination) {
      this.resetPagination()
    }
    if (this.searchCondition.reference_date === null) {
      this.searchForm.referenceDate = this.$dayjs().format("YYYY-MM-DD")
    } else {
      this.searchForm.referenceDate = this.searchCondition.reference_date
    }
    this.searchForm.code = this.searchCondition.code
    this.searchForm.name = this.searchCondition.name
    this.searchForm.itemId = this.searchCondition.item_id
    this.searchForm.salesItemType = this.searchCondition.sales_item_type
    this.page = this.pagination.current_page
    this.perPage = this.searchCondition.limit;

    this.fetchItems().then((content) => {
      console.log(content)
      this.items = content.items
    })
    this.fetchSalesItemUnits().then((content) => {
      this.salesItemUnits = content.sales_item_units
    })
    this.fetchPackings().then((content) => {
      this.packings = content.packings
    })
    this.fetchHazardousSubstances().then((content) => {
      this.hazardousSubstances = content.hazardous_substances
    })
    this.fetchTransportRoutes({ without_invisible: true, first_trader_owned: true, self_emission: true, self_emission_include_own: true }).then((content) => {
      this.transportRoutes = content.transport_routes
    })
  },
  mounted () {
    /** 初期表示の検索はcreated()の段階で検索すると、バリデーション未生成でエラーするため、mounted()で検索実行 */
    this.searchItemPrice()
  },
  methods: {
    /** api取得 */
    ...mapActions('DriverMasterSalesItemPrice', {
      fetchSalesItemPrices: 'index',
      deleteItemPrice: 'destroy',
      resetState: 'resetState',
      resetPagination: 'resetPagination',
    }),
    ...mapActions('DriverSettingSalesItemPrice', {
      download: 'index',
    }),
    /** 取扱品目 api取得*/
    ...mapActions('DriverMasterSearchItem', {
      fetchItems: 'index',
    }),
    /** 売上品目単位 api取得*/
    ...mapActions('DriverMasterSearchSalesItemUnit', {
      fetchSalesItemUnits: 'index',
    }),
    /** 荷姿 api取得*/
    ...mapActions('DriverMasterSearchPacking', {
      fetchPackings: 'index',
    }),
    /** 有害物質 api取得 */
    ...mapActions('DriverMasterSearchHazardousSubstance', {
      fetchHazardousSubstances: 'index'
    }),
    /** 運搬ルート api取得 */
    ...mapActions('DriverMasterSearchTransportRoute', {
      fetchTransportRoutes: 'search',
    }),
    /**
     * 追加ボタン クリックイベント
     */
    addButtonClickEvent () {
      this.targetId = null
      this.enableItemPriceDialog = true
    },
    /**
     * 編集ボタン クリックイベント
     * @param item { Object }
     */
    editButtonClickEvent (item) {
      this.targetId = item.id
      this.enableItemPriceDialog = true
    },
    /**
     * 削除ボタン クリックイベント
     * @param item { Object }
     */
    deleteButtonClickEvent (item) {
      this.$refs.confirm.showDeleteConfirm().then((confirm) => {
        this.$refs.confirm.close()
        if (confirm) {
          this.deleteItemPrice({ id: item.id }).then(() =>{
            this.searchItemPrice()
          })
        }
      })
    },

    importButtonClickEvent () {
      this.$router.push({name: 'DriverMasterSalesItemPriceCsvImport'})
    },
    exportButtonClickEvent () {
      let params = this.buildParam('json')
      this.download(params).then((content) => {
        this.downloadCsv(content, '売上品目単価')
      })
    },
    /**
     * モーダルを閉じる
     */
    closeItemPriceDialog () {
      this.enableItemPriceDialog = false;
    },
    /**
     * モーダルからのコールバック処理
     */
    itemPriceCallback () {
      this.closeItemPriceDialog()
      this.searchItemPrice()
    },
    changePage(page) {
      this.page = page;
      this.searchItemPrice()
    },
    changePerPage (perPage) {
      this.perPage = perPage
      this.page = 1
      this.searchItemPrice()
    },
    /**
     * 表示基準日変更　コールバック
     */
    referenceDateChangedCallback (value) {
      this.searchForm.referenceDate = value;
    },
    switchSupplierSalesItemPriceButtonClickEvent () {
      this.$router.push({name: 'DriverMasterSupplierSalesItemPrices'})
    },
    searchButtonClickEvent () {
      this.page = 1
      this.searchItemPrice()
    },
    /**
    * 売上品目単価 検索
    */
    searchItemPrice () {
      this.$refs.SalesItemPriceValidation.validate().then((result) => {
        if(result) {
          let params = this.buildParam('json')
          this.fetchSalesItemPrices(params).then((content) => {
            this.salesItemPrices = content.sales_item_prices
          })
        }
      })
    },
    buildParam (format) {
      return {
        reference_date: this.searchForm.referenceDate,
        code: this.searchForm.code,
        name: this.searchForm.name,
        item_id: this.searchForm.itemId,
        sales_item_type: this.searchForm.salesItemType,
        page: this.page,
        limit: this.perPage,
        format: format
      }
    },
  }
}
</script>

<style scoped lang="scss">
.panel-header-color {
  color: #0D74BA !important;
  & .v-expansion-panel-header__icon {
    & .v-icon {
      color: #0D74BA !important;
      &::before {
        color: #0D74BA !important;
      }
    }
  }
}
.panel-selectable {
  background-color: #DCE7EB;
}

</style>
